import { Button, Skeleton } from '@personachat/ui';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import type { KeyboardEvent, ReactNode } from 'react';

import { formatNombre } from '../lib/format';

export interface Colonne<T> {
  id: string;
  header: string;
  cell: (ligne: T) => ReactNode;
  /** Clé de tri envoyée à l'API ; absente : colonne non triable. */
  sortKey?: string;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  /** Masquée sous 1280 px. */
  secondaire?: boolean;
}

export interface Tri {
  cle: string;
  ordre: 'asc' | 'desc';
}

export interface PaginationTable {
  page: number;
  pages: number;
  total: number;
}

/**
 * Tableau de données du back-office (cahier des charges §4) : tri et
 * pagination côté serveur, sélection multiple, ligne cliquable, squelettes
 * au chargement, état vide avec action. L'erreur est gérée par l'écran.
 */
export function DataTable<T>({
  colonnes,
  lignes,
  cleLigne,
  chargement,
  vide,
  tri,
  onTri,
  selection,
  onSelection,
  onOuvrir,
  pagination,
  onPage,
  libelle,
  estActive,
}: {
  colonnes: Colonne<T>[];
  lignes: T[] | undefined;
  cleLigne: (ligne: T) => string;
  chargement: boolean;
  vide: ReactNode;
  tri?: Tri;
  onTri?: (tri: Tri) => void;
  selection?: Set<string>;
  onSelection?: (selection: Set<string>) => void;
  onOuvrir?: (ligne: T) => void;
  pagination?: PaginationTable;
  onPage?: (page: number) => void;
  libelle: string;
  /** Ligne mise en avant (panneau ouvert). */
  estActive?: (ligne: T) => boolean;
}) {
  const selectionnable = !!selection && !!onSelection;
  const idsPage = (lignes ?? []).map(cleLigne);
  const toutCoche = idsPage.length > 0 && idsPage.every((id) => selection?.has(id));
  const partiel = !toutCoche && idsPage.some((id) => selection?.has(id));

  const basculerTout = () => {
    if (!onSelection || !selection) return;
    const suivante = new Set(selection);
    if (toutCoche) idsPage.forEach((id) => suivante.delete(id));
    else idsPage.forEach((id) => suivante.add(id));
    onSelection(suivante);
  };

  const basculer = (id: string) => {
    if (!onSelection || !selection) return;
    const suivante = new Set(selection);
    if (suivante.has(id)) suivante.delete(id);
    else suivante.add(id);
    onSelection(suivante);
  };

  const trier = (c: Colonne<T>) => {
    if (!c.sortKey || !onTri) return;
    onTri({ cle: c.sortKey, ordre: tri?.cle === c.sortKey && tri.ordre === 'desc' ? 'asc' : 'desc' });
  };

  const ouvrirAuClavier = (e: KeyboardEvent, ligne: T) => {
    if (e.key === 'Enter' && onOuvrir) {
      e.preventDefault();
      onOuvrir(ligne);
    }
  };

  const nbColonnes = colonnes.length + (selectionnable ? 1 : 0);

  return (
    <div className="table-wrap">
      <table className="table" aria-label={libelle} aria-busy={chargement}>
        <thead>
          <tr>
            {selectionnable && (
              <th className="table__check">
                <input
                  type="checkbox"
                  checked={toutCoche}
                  ref={(el) => {
                    if (el) el.indeterminate = partiel;
                  }}
                  onChange={basculerTout}
                  aria-label="Tout sélectionner sur la page"
                  disabled={idsPage.length === 0}
                />
              </th>
            )}
            {colonnes.map((c) => {
              const trieIci = tri?.cle === c.sortKey && !!c.sortKey;
              return (
                <th
                  key={c.id}
                  style={{ width: c.width, textAlign: c.align }}
                  className={c.secondaire ? 'table__secondaire' : undefined}
                  aria-sort={trieIci ? (tri!.ordre === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {c.sortKey && onTri ? (
                    <button type="button" className="table__sort" onClick={() => trier(c)}>
                      {c.header}
                      {trieIci ? (
                        tri!.ordre === 'asc' ? <ArrowUp size={13} aria-hidden="true" /> : <ArrowDown size={13} aria-hidden="true" />
                      ) : (
                        <ArrowUpDown size={13} aria-hidden="true" className="table__sort-idle" />
                      )}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {chargement && !lignes
            ? Array.from({ length: 8 }, (_, i) => (
                <tr key={`squelette-${i}`}>
                  {Array.from({ length: nbColonnes }, (__, j) => (
                    <td key={j}>
                      <Skeleton width={j === 0 && selectionnable ? 14 : `${50 + ((i * 7 + j * 13) % 40)}%`} />
                    </td>
                  ))}
                </tr>
              ))
            : (lignes ?? []).map((ligne) => {
                const id = cleLigne(ligne);
                const coche = selection?.has(id) ?? false;
                return (
                  <tr
                    key={id}
                    className={[
                      onOuvrir ? 'table__row--clickable' : '',
                      coche ? 'table__row--selected' : '',
                      estActive?.(ligne) ? 'table__row--active' : '',
                    ].join(' ')}
                    onClick={onOuvrir ? () => onOuvrir(ligne) : undefined}
                    onKeyDown={onOuvrir ? (e) => ouvrirAuClavier(e, ligne) : undefined}
                    tabIndex={onOuvrir ? 0 : undefined}
                  >
                    {selectionnable && (
                      <td className="table__check" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={coche} onChange={() => basculer(id)} aria-label="Sélectionner la ligne" />
                      </td>
                    )}
                    {colonnes.map((c) => (
                      <td key={c.id} style={{ textAlign: c.align }} className={c.secondaire ? 'table__secondaire' : undefined}>
                        {c.cell(ligne)}
                      </td>
                    ))}
                  </tr>
                );
              })}
        </tbody>
      </table>
      {!chargement && lignes && lignes.length === 0 && <div className="table__empty">{vide}</div>}
      {pagination && onPage && pagination.total > 0 && (
        <div className="table__footer">
          <span>
            {formatNombre(pagination.total)} résultat{pagination.total > 1 ? 's' : ''}
            {selection && selection.size > 0 && ` · ${formatNombre(selection.size)} sélectionné${selection.size > 1 ? 's' : ''}`}
          </span>
          <div className="table__pager">
            <Button size="sm" variant="ghost" onClick={() => onPage(pagination.page - 1)} disabled={pagination.page <= 1} aria-label="Page précédente">
              <ChevronLeft size={15} aria-hidden="true" />
            </Button>
            <span>
              Page {formatNombre(pagination.page)} sur {formatNombre(Math.max(1, pagination.pages))}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onPage(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              aria-label="Page suivante"
            >
              <ChevronRight size={15} aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
